import React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import lodash from "lodash";
import Moment from "moment";
import { extendMoment } from "moment-range";

import Modal from "../../Modal";
import NotAccepted from "./NotAccepted";
import Warnings from "./Warnings";
import AdminWarnings from "./AdminWarnings";
import { AcceptCancel, ApproveDeny, ReturnToManagement } from "./Buttons";
import { formatStartEndTime } from "../../../utils/date";

const moment = extendMoment(Moment);

const MessageAlreadyAccepted = ({ show }) =>
  show ? (
    <p className="pink-text text-center">
      This Sub Request has already been accepted, please refresh your browser to
      update the Queue.
    </p>
  ) : null;

class Show extends React.Component {
  state = {
    adminNote: "",
    shifts: [],
    subUser: {},
    conflictShiftTimes: "",
    conflict: false,
    managementRequestOverMaxWeeklyHours: false,
    managementRequestOverMaxDailyHours: false
  };

  currentUser = window.Application.currentUser;
  scheduleModifyAccess = this.currentUser.scheduling_access == "scheduling_modify_access";

  componentWillReceiveProps(nextProps) {
    if (nextProps.show) {
      const { request } = nextProps;
      const startTime = this.startOfWeek(request.shift.start_time).format("YYYY-MM-DD");
      const endTime = this.endOfWeek(request.shift.end_time).format("YYYY-MM-DD");
      const url = `/shifts.json?start_time=${startTime}&end_time=${endTime}&published=true`;

      this.resetStates();

      axios.get(url).then(resp => {
        this.setState({ shifts: resp.data });

        if (request.sub_user_id) {
          axios.get(`/users/${request.sub_user_id}.json`).then(resp => {
            this.setState(
              {
                subUser: resp.data
              },
              () => {
                this.checkForConflicts(this.state.shifts, resp.data.id);
                const exceedMaxWeeklyHours = this.subUserOverMaxWeeklyHours();
                const exceedMaxDailyHours = this.subUserOverMaxDailyHours();

                this.setState({
                  managementRequestOverMaxWeeklyHours: exceedMaxWeeklyHours,
                  managementRequestOverMaxDailyHours: exceedMaxDailyHours
                })
              }
            );
          });
        } else {
          this.checkForConflicts(resp.data, this.currentUser.id);
        }
      });
    }
  }

  resetStates = () => this.setState({
    adminNote: "",
    shifts: [],
    conflictShiftTimes: "",
    subUser: {},
    conflict: false,
    managementRequestOverMaxWeeklyHours: false,
    managementRequestOverMaxDailyHours: false
  });

  render() {
    const {
      shifts,
      conflictShiftTimes,
      conflict,
      subUser,
      managementRequestOverMaxWeeklyHours,
      managementRequestOverMaxDailyHours,
      adminNote
    } = this.state;
    const { request, queue, onClose, onAction } = this.props;
    const alreadyAccepted = request && (!request.active && queue);

    return (
      <Modal title="Sub Request" visible={!!request} onClose={onClose}>
        {request ? (
          <div>
            {alreadyAccepted ? null : (
              <NotAccepted
                request={request}
                shifts={shifts}
                userHoursThisWeek={this.calculateHours(
                  lodash.filter(shifts, { user_id: request.user_id }),
                  request
                )}
                subHoursThisWeek={this.subHoursThisWeek()}
                shiftHours={this.shiftHours()}
              />
            )}

            {!request.active && !request.processed ? (
              <span>
                <label htmlFor="manager-note" className="pull-left">Manager Note</label>
                <textarea
                  id="manager-note"
                  rows="2"
                  cols="50"
                  className="form-control"
                  value={adminNote}
                  onChange={e => this.setState({ adminNote: e.target.value })}
                />
              </span>
            ) : null}

            <Warnings
              show={request.active && !alreadyAccepted}
              startOfWeek={this.startOfWeek()}
              endOfWeek={this.endOfWeek()}
              myHoursThisWeek={this.myHoursThisWeek()}
              maxWeeklyHours={this.currentUser.max_weekly_hours}
              maxDailyHours={this.currentUser.max_daily_hours}
              conflict={conflict}
              activeRequestOverMaxWeeklyHours={this.activeRequestOverMaxWeeklyHours()}
              activeRequestOverMaxDailyHours={this.activeRequestOverMaxDailyHours()}
              correctUserToAcceptRequest={this.correctUserToAcceptRequest()}
              conflictShiftTimes={conflictShiftTimes}
            />

            <MessageAlreadyAccepted show={alreadyAccepted} />

            <AdminWarnings
              show={
                !request.active && !request.processed && this.scheduleModifyAccess
              }
              maxWeeklyHours={subUser.max_weekly_hours}
              conflict={conflict}
              conflictShiftTimes={conflictShiftTimes}
              managementRequestOverMaxWeeklyHours={managementRequestOverMaxWeeklyHours}
              managementRequestOverMaxDailyHours={managementRequestOverMaxDailyHours}
              maxDailyHours={subUser.max_daily_hours}
            />
            <hr />

            <AcceptCancel
              user={this.currentUser}
              scheduleModifyAccess={this.scheduleModifyAccess}
              request={request}
              alreadyAccepted={alreadyAccepted}
              onAction={onAction}
            />

            <ApproveDeny
              user={this.currentUser}
              scheduleModifyAccess={this.scheduleModifyAccess}
              request={request}
              onAction={(e) => onAction(e, adminNote)}
            />

            <ReturnToManagement
              user={this.currentUser}
              scheduleModifyAccess={this.scheduleModifyAccess}
              request={request}
              onAction={onAction}
            />
          </div>
        ) : null}
      </Modal>
    );
  }

  startOfWeek = date => {
    let startDate = moment(new Date(date || this.props.request.shift.start_time));
    if (startDate.day() < window.Application.currentAccount.start_day_of_week) {
      startDate = startDate.subtract(7, 'd')
    };
    return startDate.startOf("week")
      .day(window.Application.currentAccount.start_day_of_week || 0);
  };

  endOfWeek = date => {
    let endDate = moment(new Date(date || this.props.request.shift.end_time));
    if (endDate.day() >= window.Application.currentAccount.start_day_of_week) {
      endDate = endDate.add(7, 'd')
    };
    return endDate.endOf("week")
      .day(window.Application.currentAccount.start_day_of_week -1 || 0);
  };

  weekOfShiftRange = () =>
    moment().range(this.startOfWeek(), this.endOfWeek());

  shiftRange = () =>
    moment.range(
      this.props.request.shift.start_time,
      this.props.request.shift.end_time
    );

  shiftHours = () => {
    return Number(this.props.request.shift.total_hours)
  };

  myHoursThisWeek = () =>
    this.calculateHours(
      lodash.filter(this.state.shifts, { user_id: this.currentUser.id }),
      this.props.request
    );

  myHoursThisDay = () => {
    const range = moment().range(
      moment(this.props.request.shift.start_time).startOf('day'),
      moment(this.props.request.shift.end_time).endOf('day')
    )
    const shifts = _.filter(this.state.shifts, (shift) => (
      (shift.user_id == this.currentUser.id) && range.contains(moment(shift.start_time))
    ))

    return this.calculateHours(shifts)
  }

  subHoursThisWeek = () =>
    this.calculateHours(
      lodash.filter(this.state.shifts, {
        user_id: this.props.request.sub_user_id
      }),
      this.props.request
    );

  subHoursThisDay = () => {
    const range = moment().range(
      moment(this.props.request.shift.start_time).startOf('day'),
      moment(this.props.request.shift.start_time).endOf('day')
    )
    const shifts = _.filter(this.state.shifts, (shift) => (
      (shift.user_id == this.props.request.sub_user_id) && range.contains(moment(shift.start_time))
    ))

    return this.calculateHours(shifts);
  }

  correctUserToAcceptRequest = () =>
    this.props.request.user_id !== this.currentUser.id;

  calculateHours = shifts => {
    let totalHours = 0;

    lodash.forEach(shifts, shift => {
      if (this.weekOfShiftRange().contains(moment(shift.start_time))) {
        totalHours += Number(shift.total_hours);
      }
    });

    return Math.round(totalHours * 100) / 100;
  };

  subUserOverMaxWeeklyHours = () => {
    if (isNaN(parseFloat(this.state.subUser.max_weekly_hours)))
      return false;

    return (this.subHoursThisWeek() + this.shiftHours()) > this.state.subUser.max_weekly_hours;
  };

  subUserOverMaxDailyHours = () => {
    if (isNaN(parseFloat(this.state.subUser.max_daily_hours)))
      return false;

    return (this.subHoursThisDay() + this.shiftHours()) > this.state.subUser.max_daily_hours;
  };

  activeRequestOverMaxWeeklyHours = () => {
    if (isNaN(parseFloat(this.currentUser.max_weekly_hours)))
      return false;

    return (this.myHoursThisWeek() + this.shiftHours()) > this.currentUser.max_weekly_hours;
  };

  activeRequestOverMaxDailyHours = () => {
    if (isNaN(parseFloat(this.currentUser.max_daily_hours)))
      return false

    return ((this.myHoursThisDay() + this.shiftHours()) > this.currentUser.max_daily_hours)
  };

  checkForConflicts = (shifts, userId) => {
    const {
      request: {
        shift_id
      }
    } = this.props;
    const filteredShifts = lodash.filter(
      shifts,
      shift => shift.user_id === userId && shift.id !== shift_id
    );

    lodash.forEach(filteredShifts, shift => {
      if (this.weekOfShiftRange().contains(moment(shift.start_time))) {
        if (
          moment
            .range(shift.start_time, shift.end_time)
            .overlaps(this.shiftRange())
        ) {
          this.setState({
            conflict: true,
            conflictShiftTimes: formatStartEndTime(
              shift.start_time,
              shift.end_time
            )
          });
        }
      }
    });
  };
}

Show.propTypes = {
  show: PropTypes.bool,
  request: PropTypes.object,
  queue: PropTypes.bool,
  onClose: PropTypes.func,
  onAction: PropTypes.func
};

export default Show;
